import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  Inject,
  input,
  PLATFORM_ID,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import {SearchWidgetV3StateService} from '../../services/search-widget-v3-state.service';
import {UntypedFormBuilder} from '@angular/forms';
import {WidgetCssClassNameEnum} from '../../enums/search-widget-css-classes.enum';

enum ScrollDirectionEnum {
  UP = 'up',
  STATIC = 'static',
  DOWN = 'down',
}

@Component({
  selector: 'app-search-widget-v3-desktop',
  templateUrl: './search-widget-v3-desktop.component.html',
  styleUrls: ['./search-widget-v3-desktop.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'hostClasses()',
  },
})
export class SearchWidgetV3DesktopComponent {
  isOnTop = input<boolean>(true);
  isInline = input<boolean>(false);
  public scrollingStatus = signal<ScrollDirectionEnum>(ScrollDirectionEnum.STATIC);
  public widgetCssClassName = signal<string>(`${WidgetCssClassNameEnum.TOP} ${WidgetCssClassNameEnum.RELATIVE}`);
  hostClasses = computed(() => {
    if (this.isInline()) {
      return 'search-widget-v3-desktop flex flex-col bg-transparent relative w-full search-widget-inline';
    } else {
      return 'search-widget-v3-desktop flex flex-col bg-white z-10 border-b border-b-grey-8 top-0 left-1/2 -translate-x-1/2 w-screen relative pt-2';
    }
  });
  protected freeSearchForm;
  private widgetState = inject(SearchWidgetV3StateService);
  public placesFieldValue = this.widgetState.placesFieldValue;
  public freeSearchFieldValue = this.widgetState.freeSearchFieldValue;
  private isSearchWidgetExpanded = this.widgetState.isSearchWidgetExpanded;
  private lastScrollTop = signal<number>(0);
  private formBuilder = inject(UntypedFormBuilder);

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    this.buildFreeSearchForm();
    effect(
      () => {
        if (this.isOnTop() === true && this.scrollingStatus() !== ScrollDirectionEnum.DOWN) {
          this.widgetCssClassName.set(`${WidgetCssClassNameEnum.TOP}`);
        } else {
          this.widgetCssClassName.set(
            `${
              this.scrollingStatus() === ScrollDirectionEnum.UP
                ? WidgetCssClassNameEnum.UP
                : WidgetCssClassNameEnum.NORMAL
            } ${this.isInline() ? '' : 'shadow-lg'}`,
          );
        }
      },
      {allowSignalWrites: true},
    );

    effect(() => {
      this.freeSearchForm.setValue(
        {
          term: this.freeSearchFieldValue(),
        },
        {emitEvent: false},
      );
    });
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(event) {
    const verticalOffset = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    let scrollDirection: ScrollDirectionEnum;
    if (verticalOffset < this.lastScrollTop()) {
      scrollDirection = ScrollDirectionEnum.UP;
    } else if (verticalOffset > this.lastScrollTop()) {
      scrollDirection = ScrollDirectionEnum.DOWN;
    } else {
      scrollDirection = ScrollDirectionEnum.STATIC;
    }

    this.scrollingStatus.set(scrollDirection);
    this.lastScrollTop.set(verticalOffset <= 0 ? 0 : verticalOffset);
  }

  public toggleSearchWidget() {
    this.widgetState.updateIsSearchWidgetExpanded(!this.isSearchWidgetExpanded());
  }

  public onFreeSearchClick(): void {
    this.widgetState.updateShouldFocusPlacesField(false);
    this.toggleSearchWidget();
  }

  public onPlaceClick(): void {
    this.widgetState.updateShouldFocusPlacesField(true);
    this.toggleSearchWidget();
  }

  private buildFreeSearchForm() {
    this.freeSearchForm = this.formBuilder.group({
      term: [''],
    });
  }
}
